import { z } from 'zod';
import { ChatMessage, Modality, ModelRequestPurpose, ReasoningEffort, TokenCountSource, ToolCall, ToolDefinition } from './domain.js';
import type { AnchorTags, TokenScope } from './anchor.js';

// The ModelGateway seam. Shared by @jabberr/gateway, @jabberr/api and tests.

export const GatewayEvent = z.discriminatedUnion('type', [
  z.object({ type: z.literal('reasoning'), text: z.string() }),
  z.object({ type: z.literal('content'), text: z.string() }),
  /** The engine's top alternatives for one generated content token; only when the request set topLogprobs. */
  z.object({ type: z.literal('logprobs'), top: z.array(z.object({ token: z.string(), logprob: z.number() })) }),
  /** One complete call, assembled from the streamed fragments. The done event that follows says 'tool_calls'. */
  z.object({ type: z.literal('tool_call'), call: ToolCall }),
  z.object({
    type: z.literal('usage'),
    promptTokens: z.number().int().nonnegative(),
    cachedPromptTokens: z.number().int().nonnegative().optional(),
    reasoningTokens: z.number().int().nonnegative().optional(),
    completionTokens: z.number().int().nonnegative(),
  }),
  z.object({
    type: z.literal('done'),
    finishReason: z.enum(['stop', 'length', 'tool_calls', 'cancelled', 'error']),
    error: z.string().optional(),
  }),
]);
export type GatewayEvent = z.infer<typeof GatewayEvent>;
export type GatewayFinishReason = Extract<GatewayEvent, { type: 'done' }>['finishReason'];

export const GatewayRequestMeta = z.object({
  conversationId: z.string(),
  /** Null only for a keen decision on a meeting utterance that never became a turn (OPEN-31). */
  turnId: z.string().nullable(),
  projectId: z.string(),
  userId: z.string(),
  modality: Modality,
  prefixHash: z.string(),
  contextVersion: z.number().int(),
});
export type GatewayRequestMeta = z.infer<typeof GatewayRequestMeta>;

// Not a zod schema: carries an AbortSignal.
export interface GatewayRequest {
  requestId: string;
  messages: ChatMessage[];
  reasoningEffort: ReasoningEffort;
  enableThinking: boolean;
  clearThinking?: boolean;
  maxOutputTokens?: number;
  /** Ask the engine for this many alternatives per generated token (a 'logprobs' event each). The keen decider
   *  reads P(yes) from the first token instead of trusting one sampled word. */
  topLogprobs?: number;
  /** Engine scheduling priority, lower first (vLLM --scheduling-policy priority). Sent only when set, and only on
   *  the glm-recipe profile: an engine without the policy rejects any nonzero value. */
  priority?: number;
  /** Tools the model may call this round. Present only for conversations with web search on. */
  tools?: ToolDefinition[];
  /** Default 'reply'. Recorded on model_requests.purpose; RecordedProvider looks for <purpose>.json. */
  purpose?: ModelRequestPurpose;
  /** Account, project and audience for the Compute gateway's X-Anchor-* headers (OPEN-36). Sent only when
   *  INFERENCE_GATEWAY_TOKEN is set; the api fills it in (services/anchor-tags.ts). */
  anchor?: AnchorTags;
  signal: AbortSignal;
  meta: GatewayRequestMeta;
}

/** Per-deployment prompt limits (INFERENCE_MODEL_WINDOW, INFERENCE_MAX_OUTPUT_TOKENS). */
export interface GatewayLimits {
  modelWindow: number;
  maxOutputTokens: number;
}

export interface TokenCount {
  tokens: number;
  source: TokenCountSource;
}

export interface GatewayHealth {
  ok: boolean;
  latencyMs: number;
  model: string;
  detail?: string;
}

export interface ModelGateway {
  readonly deploymentId: string;
  readonly model: string;
  readonly limits: GatewayLimits;
  stream(req: GatewayRequest): AsyncIterable<GatewayEvent>;
  health(): Promise<GatewayHealth>;
  /** Tokens in `text` as the engine counts them, or an estimate when the tokenizer endpoint is absent or slow.
   *  scope: whose text this is (OPEN-36). Through the Compute gateway it supplies the X-Anchor-* tags and picks the
   *  credential; text with no scope, unresolvable tags, or an audience with no credential is only estimated locally,
   *  so client text never travels on the internal credential. */
  countTokens(text: string, scope?: TokenScope): Promise<TokenCount>;
}

export const InferenceProvider = z.enum(['openai-compatible', 'recorded']);
export type InferenceProvider = z.infer<typeof InferenceProvider>;

export const InferenceProfile = z.enum(['glm-recipe', 'generic']);
export type InferenceProfile = z.infer<typeof InferenceProfile>;

// Recorded fixture file shape for RecordedProvider (packages/gateway).
export const RecordedFixture = z.object({
  events: z.array(GatewayEvent).default([]),
  delayMs: z.number().int().nonnegative().default(0),
  /** When true, the last user message is replayed as content before `events` (tests of the summary checkpoint). */
  echo: z.boolean().default(false),
  /** With echo: replay at most this many characters of the last user message. */
  echoMaxChars: z.number().int().positive().optional(),
});
export type RecordedFixture = z.infer<typeof RecordedFixture>;
